import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import axios from 'axios';

interface PreSignedUrlResponse {
  success: boolean;
  data: {
    presignedUrl: string; // PUT target for the file upload
    key: string;          // S3 object key (stored on the tenant after upload)
  };
}

interface UploadLogoInput {
  file: File;
}

/**
 * Mutation hook for uploading a company logo.
 * Requests a pre-signed PUT URL from GET/POST /tenant/me/logo,
 * then uploads the file directly to S3/MinIO.
 */
export function useTenantLogoUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file }: UploadLogoInput) => {
      // 1. Get pre-signed URL from API
      const response = await apiClient.post<PreSignedUrlResponse>('/tenant/me/logo', {
        contentType: file.type,
        fileName: file.name,
      });
      const { presignedUrl, key } = response.data.data;

      // 2. Upload file directly to S3/MinIO via PUT
      await axios.put(presignedUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      return key;
    },
    onSuccess: () => {
      // Invalidate queries to refresh logo
      queryClient.invalidateQueries({ queryKey: queryKeys.tenant.me() });
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() });
    },
  });
}
